import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './StaticPage.css';

function AboutPage() {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="static-page">
      <nav className={`nav ${scrolled ? 'nav-scrolled' : ''}`}>
        <div className="nav-container">
          <a href="/" className="nav-logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
          <div className="nav-links">
            <a href="/">Home</a>
            <a href="/platform">Platform</a>
            <a href="/how-it-works">How it Works</a>
            <a href="/use-cases">Use Cases</a>
            <a href="/consulting">Consulting</a>
          </div>
          <div className="nav-actions">
            <button className="btn-ghost" onClick={() => navigate('/login')}>Sign In</button>
            <button className="btn-primary" onClick={() => navigate('/request-demo')}>Request a Demo</button>
          </div>
        </div>
      </nav>

      <section className="sp-hero">
        <div className="sp-container">
          <h1 className="sp-hero-title">About CerebralMachines</h1>
          <p className="sp-hero-text">
            We are building the foundational cognitive infrastructure that enterprises need to make AI truly work.
          </p>
        </div>
      </section>

      <section className="sp-section">
        <div className="sp-container">
          <h2 className="sp-section-title">Our Mission</h2>
          <p className="sp-text">
            CerebralMachines was founded on a simple but powerful insight: enterprises don't need more AI models.
            They need the missing cognitive layer that makes AI precise, controllable, explainable, and trustworthy
            enough for mission-critical operations.
          </p>
          <p className="sp-text">
            Our mission is to provide that layer — enabling every enterprise to build, deploy, and govern
            AI systems that reason like their best experts, at any scale, with full determinism and auditability.
          </p>
        </div>
      </section>

      <section className="sp-section sp-section-alt">
        <div className="sp-container">
          <h2 className="sp-section-title">What We Believe</h2>
          <div className="sp-cards">
            <div className="sp-card">
              <h3>AI Must Be Controllable</h3>
              <p>
                Enterprises cannot adopt AI that cannot be inspected, overridden, or audited.
                Every decision must be traceable back to verifiable logic.
              </p>
            </div>
            <div className="sp-card">
              <h3>Domain Knowledge Is the Key</h3>
              <p>
                Language models understand language. But enterprises need systems that understand
                their policies, rules, exceptions, and workflows — with precision.
              </p>
            </div>
            <div className="sp-card">
              <h3>Real Impact Over Demos</h3>
              <p>
                We measure success by production deployments that transform business processes,
                not by impressive prototypes that never ship.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="sp-section">
        <div className="sp-container">
          <h2 className="sp-section-title">Our Story</h2>
          <p className="sp-text">
            CerebralMachines emerged from years of deep research in semantic computing, cognitive architectures,
            and enterprise systems. Our founders saw the same pattern across industries: billions invested in AI,
            but the vast majority of projects stuck in pilot, failing to reach production.
          </p>
          <p className="sp-text">
            The root cause was always the same — AI systems that couldn't actually encode, apply, and enforce
            the rich domain knowledge that enterprises depend on. We set out to solve that problem from first
            principles, building a new category of technology: the Hybrid Cognitive Linguistic Core.
          </p>
          <p className="sp-text">
            Today, CerebralMachines powers enterprise reasoning at scale across healthcare, financial services,
            insurance, and government — delivering deterministic, explainable, and governable AI that
            executives can trust and auditors can verify.
          </p>
        </div>
      </section>

      <section className="sp-cta">
        <div className="sp-container">
          <h2 className="sp-cta-title">Want to Learn More?</h2>
          <p className="sp-cta-text">
            See how CerebralMachines can transform your AI strategy.
          </p>
          <button className="btn-primary-large" onClick={() => navigate('/request-demo')}>Request a Demo</button>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-container">
          <div className="footer-main">
            <div className="footer-brand">
              <a href="/" className="footer-logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
              <p className="footer-tagline">Building the cognitive backbone for enterprise AI.</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Product</h4>
                <ul>
                  <li><a href="/platform">Platform</a></li>
                  <li><a href="/how-it-works">How it Works</a></li>
                  <li><a href="/use-cases">Use Cases</a></li>
                  <li><a href="/consulting">Consulting</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About</a></li>
                  <li><a href="/team">Team</a></li>
                  <li><a href="/careers">Careers</a></li>
                  <li><a href="/contact">Contact</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Legal</h4>
                <ul>
                  <li><a href="/privacy">Privacy Policy</a></li>
                  <li><a href="/terms">Terms of Service</a></li>
                  <li><a href="/security">Security</a></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 CerebralMachines. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default AboutPage;
